import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { noop } from 'lodash'
import { Button } from '@/components/button'
import { createModal, useModalize } from '@/components/modalize/mui'
import useMediaQuery from '@/hooks/use-media-query'
import { fontWeights, breakpoints, fontSizes } from '@/styles'
import ModalBase from '@/components/modal/modal-base'
import Context from '@/components/modal/context'

const Wrapper = styled.div`
  margin: 0 8px 8px 8px;
`

const TextRow = styled.div`
  p {
    font-size: ${fontSizes.regular};
    font-weight: ${fontWeights.regular};
    margin: 0;
    letter-spacing: -0.39px;
    line-height: 20px;
  }

  ul {
    padding-inline-start: 20px;
    margin-block-end: 24px;
    li {
      margin: 20px 0;
    }
  }
`
const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  width: 100%;
  button:nth-child(2) {
    margin-left: 20px;
  }
`

const ReviewTermsModal = ({ listingId, history, ModalTitle, ModalFooter, onBack = noop }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const handleSubmit = async () => {
    setIsLoading(true)
    const { error, body } = await dispatch.contract.createCustomContract({
      listingId
    })
    setIsLoading(false)
    if (!error) {
      close()
      history.replace(`/custom-contract/create/${body?.id}`)
    }
  }

  const handleClose = () => {
    close()
    onBack()
  }

  return (
    <Context.Provider
      value={{
        ModalTitle,
        ModalFooter,
        close: handleClose
      }}
    >
      <ModalBase
        overflow="visible"
        title={t('l.contract.create.custom_contract_terms.title', 'Custom Contract Terms')}
      >
        <Wrapper>
          <TextRow>
            <ul>
              <li>
                {t(
                  'l.contract.create.custom_contract_terms_1.text',
                  'Landlord is responsible for ensuring that the uploaded document complies with the applicable law. Liv Strategies Inc. is not responsible for verifying the legality of the uploaded document.'
                )}
              </li>
              <li>
                {t(
                  'l.contract.create.custom_contract_terms_2.text',
                  'Landlord is responsible for ensuring that the information contained in the uploaded document has been accurately entered by the Landlord on the Platform.'
                )}
              </li>
              <li>
                {t(
                  'l.contract.create.custom_contract_terms_3.text',
                  `Landlord acknowledges that Liv Strategies Inc.'s services to the Landlord and Tenant will be provided on the basis of the information manually entered in the Platform by the Landlord and not on the basis of the information contained in the uploaded document.`
                )}
              </li>
              <li>
                {t(
                  'l.contract.create.custom_contract_terms_4.text',
                  `Landlord acknowledges that its legal relations with the tenant will be governed by its written agreements with the tenant and by the applicable law and not by the information entered into the platform by the Landlord.`
                )}
              </li>
              <li>
                {t(
                  'l.contract.create.custom_contract_terms_5.text',
                  `Landlord responsible for confirming that the tenancy agreements may be executed electronically in the Landlord’s jurisdiction and the Tenant’s jurisdiction.`
                )}
              </li>
            </ul>
          </TextRow>
          <ButtonGroup>
            <Button
              type="button"
              theme="secondary"
              onClick={handleClose}
              disabled={isLoading}
              size={displayMobile ? 'medium' : 'small'}
              width={displayMobile ? '100%' : 122}
            >
              {t('common.back', 'Back')}
            </Button>

            <Button
              type="button"
              theme="primary"
              onClick={handleSubmit}
              disabled={isLoading}
              size={displayMobile ? 'medium' : 'small'}
              width={displayMobile ? '100%' : 122}
            >
              {t('l.payments.view.i_agree.button', 'I Agree')}
            </Button>
          </ButtonGroup>
        </Wrapper>
      </ModalBase>
    </Context.Provider>
  )
}

export const show = createModal(ReviewTermsModal, {
  width: '726px',
  sheet: true,
  clickOutside: true
})
